import { useNavigate } from 'react-router-dom';
import { useAuth } from '../AuthContext';
import { useTheme } from '../theme';
import { makeAuthStyles } from './authStyles';

// Home-screen-style launcher. A grid of app tiles (icon + title) that open the
// live radar, settings, a test fly-by, or the pilot's past incidents.

// Tile icons — small monochrome SVGs that inherit the tile's text color.
function LiveIcon({ size = 30 }) {
  // Radar sweep = the live TCAS display.
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor"
         strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" style={{ display: 'block' }}>
      <circle cx="12" cy="12" r="9" />
      <circle cx="12" cy="12" r="4.5" opacity="0.5" />
      <line x1="12" y1="12" x2="19" y2="6.5" />
      <circle cx="16" cy="9" r="1.1" fill="currentColor" stroke="none" />
    </svg>
  );
}
function GearIcon({ size = 30 }) {
  // Settings.
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor"
         strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" style={{ display: 'block' }}>
      <circle cx="12" cy="12" r="3.2" />
      <path d="M12 2.5v3M12 18.5v3M2.5 12h3M18.5 12h3M5 5l2.1 2.1M16.9 16.9 19 19M19 5l-2.1 2.1M7.1 16.9 5 19" />
    </svg>
  );
}
function FlybyIcon({ size = 30 }) {
  // Warning triangle = test fly-by with alert.
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor"
         strokeWidth="1.8" strokeLinejoin="round" style={{ display: 'block' }}>
      <path d="M12 3.5 L21.5 20.5 L2.5 20.5 Z" />
      <line x1="12" y1="10" x2="12" y2="14.5" strokeLinecap="round" />
      <circle cx="12" cy="17.3" r="0.7" fill="currentColor" stroke="none" />
    </svg>
  );
}
function HistoryIcon({ size = 30 }) {
  // Clock with a back-arrow = past incidents.
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor"
         strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" style={{ display: 'block' }}>
      <path d="M3.5 12a8.5 8.5 0 1 1 2.5 6" />
      <polyline points="3 13 3.5 18 8 17.5" />
      <polyline points="12 7.5 12 12 15 14" />
    </svg>
  );
}
function LogoutIcon({ size = 30 }) {
  // Door with an out-arrow = log out.
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor"
         strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" style={{ display: 'block' }}>
      <path d="M15 3H6a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h9" />
      <polyline points="10 8 15 12 10 16" />
      <line x1="15" y1="12" x2="21" y2="12" />
    </svg>
  );
}

export default function MenuPage() {
  const { logout } = useAuth();
  const { colors } = useTheme();
  const C = colors;
  const S = makeAuthStyles(C);
  const navigate = useNavigate();

  const doLogout = () => { logout(); navigate('/login', { replace: true }); };

  const tiles = [
    { key: 'live',      title: 'LIVE',           Icon: LiveIcon,    onClick: () => navigate('/') },
    { key: 'settings',  title: 'SETTINGS',       Icon: GearIcon,    onClick: () => navigate('/settings') },
    // Opens the setup page (speed / closest pass / start range); it fires the
    // fly-by and sends us on to the radar.
    { key: 'flyby',     title: 'TEST FLYBY',     Icon: FlybyIcon,   onClick: () => navigate('/flyby') },
    { key: 'incidents', title: 'PAST INCIDENTS', Icon: HistoryIcon, onClick: () => navigate('/incidents') },
    { key: 'logout',    title: 'LOG OUT',        Icon: LogoutIcon,  onClick: doLogout },
  ];

  const tileStyle = {
    background: C.surface, border: `1px solid ${C.border}`, borderRadius: 14,
    aspectRatio: '1 / 1', display: 'flex', flexDirection: 'column',
    alignItems: 'center', justifyContent: 'center', gap: 12, cursor: 'pointer',
    color: C.textStrong, padding: 8,
  };

  return (
    <div style={S.page}>
      <div style={{ ...S.card, position: 'relative' }}>
        <button
          type="button" onClick={() => navigate('/')} title="Back to radar" aria-label="Back to radar"
          style={{ ...S.buttonAlt, position: 'absolute', top: 14, left: 14, width: 'auto', margin: 0,
                   padding: '6px 11px', fontSize: 18, lineHeight: 1 }}
        >
          ←
        </button>
        <h1 style={S.title}>Menu</h1>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          {tiles.map(({ key, title, Icon, onClick }) => (
            <button key={key} type="button" onClick={onClick} style={tileStyle}>
              <Icon />
              <span style={{ fontSize: 12, fontWeight: 800, letterSpacing: 0.5, color: C.textStrong }}>
                {title}
              </span>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}
